
import type { IPlayer } from "@ihs-widget/components/StatsConsole/Widgets/LineupWidget/parts/Players";
import { useContext, useState, useEffect, useRef } from 'react';
import { FieldContainerRefContext } from '@ihs-widget/components/StatsConsole/Widgets/LineupWidget/parts/FootballField';
import { type IState, StateContext } from "../../../../../state/stateContext";
import PlayerTooltip from "@ihs-widget/components/StatsConsole/Widgets/LineupWidget/parts/PlayerTooltip/PlayerTooltip";
import { useColorContext } from "@ihs-widget/stores/ColorContext";
import { useVersion } from "@ihs-widget/utils/hooks/useVersion";
import ShirtV2 from "@ihs-widget/components/StatsConsole/Widgets/LineupWidget/parts/ShirtV2";

interface OnePlayerProps {
    team: 'home' | 'away';
    width: string;
    player: IPlayer;
    id: string;
    isHorizontalList?: boolean;
    longName?: boolean;
    isInjuries?: boolean;
    isSmallShirt?: boolean;
    isLineup?: boolean;
}

const OnePlayer = ({ team, width, player, id, isHorizontalList = false, longName = false, isInjuries = false, isSmallShirt = false, isLineup = false }: OnePlayerProps) => {
    const ctx = useContext(StateContext);
    const { stContext, setStContext } = ctx || {};
    const { activeTooltipPlayerId } = stContext || {};
    const [showTooltip, setShowTooltip] = useState(false);
    const { fieldContainerRef } = useContext(FieldContainerRefContext);
    const playerRef = useRef<HTMLDivElement>(null);
    const [playerCenterX, setPlayerCenterX] = useState(0);
    const { colors } = useColorContext();
    const { isV2 } = useVersion();

    // Function to display the last name (last word of the full name)
    const getDisplayName = (fullName: string | undefined) => {
        if (!fullName) return '';
        if (isV2) {
            const nameParts = fullName.trim().split(' ');
            return nameParts[nameParts.length - 1] || fullName;
        }
        return fullName;
    };

    const handleClickShirt = () => {
        if (activeTooltipPlayerId === id) {
            setStContext((prev: IState) => ({
                ...prev,
                activeTooltipPlayerId: null
            }));
        } else {
            setStContext((prev: IState) => ({
                ...prev,
                activeTooltipPlayerId: id
            }));
        }
    };

    useEffect(() => {
        setShowTooltip(activeTooltipPlayerId === id);
    }, [activeTooltipPlayerId, id]);

    useEffect(() => {
        if (playerRef.current && fieldContainerRef?.current && showTooltip) {
            const playerRect = playerRef.current.getBoundingClientRect();
            const containerRect = fieldContainerRef.current.getBoundingClientRect();
            const centerX = playerRect.left + (playerRect.width / 2) - containerRect.left;
            setPlayerCenterX(centerX);
        }
    }, [showTooltip, fieldContainerRef]);

    return (
        <div
            ref={playerRef}
            className="relative"
            data-testid={`one-player-wrapper-${team}`}
        >
            <div data-testid={`one-player-${team}`} className={`h-[68px] flex flex-col justify-start items-center`}
                style={{ width: `${width}`, minWidth: `${width}` }}>
                <div data-testid={`one-player`} className={`${isSmallShirt ? 'w-[58px]' : 'w-[65px]'} cursor-pointer`}>
                    <div data-testid={`one-player-shirt`} className={`w-full h-[52px] flex justify-center items-center`} onClick={handleClickShirt}>
                        <ShirtV2 team={team} player={player} isHorizontalList={isHorizontalList} isInjuries={isInjuries} isSmallShirt={isSmallShirt} isLineup={isLineup} />
                    </div>
                    <div data-testid={`one-player-name`} className={`w-full h-[16px] block overflow-hidden
                            font-robotoRegular font-normal text-[12px] leading-[16px]`}
                        style={{ color: colors?.textColor }}
                    >
                        <div data-testid={`one-player-name-wrapper`} className={`
                            ${longName ? `absolute left-1/2 -translate-x-1/2 w-max` : 'w-full'} 
                            flex justify-center items-center
                        `}
                            style={{ maxWidth: `${width}`, color: isLineup && isV2 ? '#FFFFFF' : colors?.textColor }}>
                            <span data-testid={`one-player-name-text`} className="truncate">{getDisplayName(player?.name)}</span>
                            {player?.isCaptain && !isHorizontalList && <span data-testid={`one-player-name-captain`} className="shrink-0">&nbsp;(C)</span>}
                        </div>
                    </div>
                </div>
            </div>
            {showTooltip && !isHorizontalList && (
                <PlayerTooltip
                    player={player}
                    parentWidth={width}
                    containerRef={fieldContainerRef || playerRef}
                    playerCenterX={playerCenterX - 7}
                    setShowTooltip={setShowTooltip}
                    isHorizontalList={false}
                />
            )}
        </div>
    )
}

export default OnePlayer;